import cx from "classnames";
import React from "react";

import CommentHeaderName from "@/mk2/apps/blogs/components/CommentHeaderName";
import CommentRepliedToMessage from "@/mk2/apps/blogs/components/CommentRepliedToMessage";
import { blogsProfileUrl, blogsUrl } from "@/mk2/apps/blogs/urls";
import { getGroupHashtagLink } from "@/mk2/apps/blogs/utils";
import { COUNSELLING_CATEGORY_ID } from "@/mk2/apps/forum/constants";
import { PostEntity } from "@/mk2/apps/forum/schemas";
import { forumUntrustworthyUserFeedUrl } from "@/mk2/apps/forum/urls";
import { largestAvailablePhotoVersionOfPost } from "@/mk2/apps/forum/utils";
import { RenderSnippetAs } from "@/mk2/apps/wiki/utils";
import { Btn, BtnSidePadding, BtnType } from "@/mk2/components/Btn";
import { DeletedCommentAlert } from "@/mk2/components/DeletedCommentAlert";
import { Dot } from "@/mk2/components/Dot";
import { Emojify } from "@/mk2/components/Emojify";
import { Img } from "@/mk2/components/Img";
import { Link } from "@/mk2/components/Link";
import { StarsRating } from "@/mk2/components/StarsRating";
import Sticker, { StickerSize } from "@/mk2/components/Sticker";
import { ComplaintTargetType } from "@/mk2/containers/Complaint/Complaint.schemas";
import Complaints from "@/mk2/containers/Complaints/Complaints";
import {
    FilterAdditionalData,
    astToHtml,
    defaultFilterAdditionalData,
} from "@/mk2/helpers/article_ast/filters";
import {
    getAllDescendantsOfAType,
    getAttributesDict,
    removeQuotes,
} from "@/mk2/helpers/article_ast/utils";
import { cacheLast } from "@/mk2/helpers/cache";
import { DeletedByRole } from "@/mk2/helpers/deleteable";
import { url } from "@/mk2/helpers/urls";
import { BlogPostEntity, CommentEntity, PhotoEntity, UserEntity } from "@/mk2/schemas";
import { RouteNavigateFunction } from "@/mk2/services/browserHistory";
import {
    ADMIN_USER,
    AUTHOR,
    BLOGPOST_COMMENT_DELETED_BY_AUTHOR,
    BLOGPOST_COMMENT_DELETED_BY_SOMEONE,
    BLOGS_CANT_SEE_ANSWER,
    COMMENT_DELETED_BY_ADMIN,
    COMMENT_DELETED_BY_AUTHOR,
    FORUM_CANT_SEE_ANSWER,
    GROUP_COMMENT_REMOVE_COMPLAINTS,
    SHOW,
} from "@/mk/autogenerated/translations/CommentBody.7f6d2793b7f84bf6d35f3f282b1b884e";
import { hashize } from "@/mk/bazaar/common/filters/hashize";
import { photoUrl } from "@/mk/photo/photoUrl";

import styles from "./CommentBody.mscss";

interface OwnProps {
    comment: CommentEntity;
    post: BlogPostEntity | PostEntity;
    isCollapsed: boolean;
    inForum: boolean;
    inGroups: boolean;
    followLinks: boolean;
    moderators?: UserEntity[];
    hasHiddenText: boolean;
    hasDeletedStatus: boolean;
    isAdmin: boolean;
    isModerator: boolean;
    isPostAuthor: boolean;
    isMyAnonymousComment?: boolean;
    routeNavigate: RouteNavigateFunction;

    onToggleDeletedDetails?();
}

type Props = OwnProps;

export default class CommentBody extends React.PureComponent<Props> {
    private astToHtmlCache = cacheLast(true);

    public render() {
        const {
            comment,
            followLinks,
            hasDeletedStatus,
            hasHiddenText,
            inForum,
            inGroups,
            isAdmin,
            isPostAuthor,
            isCollapsed,
            isModerator,
            moderators,
            isMyAnonymousComment,
            onToggleDeletedDetails,
            post,
            routeNavigate,
        } = this.props;

        const inBlogs = !(inForum || inGroups);
        const inCounsellings =
            inForum && (post as PostEntity).topic.category.id === COUNSELLING_CATEGORY_ID;

        const inlinedImageNumbers = [];
        let belowImages = [];

        let groupId = null;
        if (inGroups && post && "group" in post) {
            const group = (post as BlogPostEntity).group;
            if (group) {
                groupId = group.id;
            }
        }

        // prepare comment message body
        let bodyChunks: React.ReactChild[] = null;

        if (comment.textAST) {
            const additionalData: FilterAdditionalData = {
                ...defaultFilterAdditionalData,
                renderSnippetObjectsAs: inCounsellings
                    ? RenderSnippetAs.HTML_IFRAME
                    : RenderSnippetAs.HTML_LINK,
                renderSpecialWikiLinks: false,
                renderLinksWithNofollow: !followLinks,
                renderLinksOfUntrustworthy: comment.authorUser.isUntrustworthy
                    ? url(forumUntrustworthyUserFeedUrl, { username: comment.authorUser.username })
                    : false,
                linkedObjects: comment.textASTAdditionalData
                    ? comment.textASTAdditionalData.linkedObjects
                    : {},
                strollerBoxes: comment.textASTAdditionalData
                    ? comment.textASTAdditionalData.strollerBoxes
                    : {},
                extraEntities: {
                    wikiArticleArray: comment.extraEntitiesOfArticle,
                    strollerArray: comment.extraEntitiesOfStroller,
                    blogPostArray: comment.extraEntitiesOfPost,
                },
                storeEventParamsOfSourceContent: inCounsellings
                    ? {
                          category_id: COUNSELLING_CATEGORY_ID,
                          post_id: post.id,
                      }
                    : null,
                hasResponsiveImages: true,
                photos: comment.photos,
                customStyles: {
                    photo: styles.CommentBody__responsivePhoto,
                },
                rootDiv: false,
                selectPhotoVersionFn: inForum
                    ? (photo) => largestAvailablePhotoVersionOfPost(photo, "s600x600")
                    : defaultFilterAdditionalData.selectPhotoVersionFn,
                hashizeFn: this.getHashizeFn(groupId, inGroups, inForum),
            };
            bodyChunks = this.astToHtmlCache(
                () => astToHtml(comment.textAST, additionalData),
                comment.textAST,
            );

            if (inForum) {
                const picNodes = getAllDescendantsOfAType(comment.textAST, "pic");
                for (const picNode of picNodes) {
                    const attributes = getAttributesDict(picNode);
                    const picCd = removeQuotes(
                        attributes.hasOwnProperty("code")
                            ? attributes.code
                            : attributes.hasOwnProperty("cd")
                              ? attributes.cd
                              : "",
                    );
                    if (picCd !== "") {
                        const inlinedImageNumber = comment.photos.findIndex(
                            (value: PhotoEntity) => {
                                return value.code === picCd;
                            },
                        );
                        if (inlinedImageNumber !== -1) {
                            inlinedImageNumbers.push(inlinedImageNumber);
                        }
                    }
                }
            }
            belowImages = comment.photos
                ? comment.photos.filter((photo, idx) => !inlinedImageNumbers.includes(idx))
                : [];
        }

        const showDeletedStatus = hasDeletedStatus && comment.deletedAt && !isCollapsed;
        const showDeletedMessage = comment.deletedAt && isCollapsed;
        const showComplaints = inGroups && !comment.deletedAt && (isAdmin || isModerator);

        return (
            // admins are using strange JS code bookmarklet to fetch usernames
            <div
                className={cx(
                    styles.CommentBody,
                    inGroups && styles["CommentBody--inGroups"],
                    inGroups && comment.byAdmin && styles["CommentBody--inGroups--admin"],
                    inGroups &&
                        showDeletedMessage &&
                        styles["CommentBody--inGroups--deletedCollapsed"],
                    inForum && styles["CommentBody--inForum"],
                )}
            >
                {showDeletedStatus && (
                    <DeletedCommentAlert
                        heading={
                            comment.deletedByRole === DeletedByRole.DeletedByAuthor
                                ? COMMENT_DELETED_BY_AUTHOR
                                : COMMENT_DELETED_BY_ADMIN
                        }
                        deletedByRole={comment.deletedByRole}
                        deletedBy={comment.deletedBy?.username}
                        deletedAt={comment.deletedAt}
                        reason={comment.deletedReason}
                    />
                )}

                {inGroups && showDeletedMessage && (
                    <div className={styles.CommentBody__deletedCollapsed}>
                        {comment.deletedByRole === DeletedByRole.DeletedByAuthor
                            ? BLOGPOST_COMMENT_DELETED_BY_AUTHOR
                            : BLOGPOST_COMMENT_DELETED_BY_SOMEONE}
                        {bodyChunks && (
                            <>
                                &nbsp;&bull;&nbsp;
                                <Btn
                                    key="deleted-btn"
                                    onClick={onToggleDeletedDetails}
                                    label={SHOW}
                                    sidePadding={BtnSidePadding.None}
                                    type={BtnType.BlueTextLight}
                                    classNames={{
                                        label: styles.CommentBody__deletedBtnLabel,
                                    }}
                                />
                            </>
                        )}
                    </div>
                )}

                <div
                    className={
                        // class comment2-main kept here to retain working some custom admin scripts
                        cx({ "comment2-main": !inForum })
                    }
                >
                    {!hasHiddenText
                        ? [].concat(
                              ...[
                                  (!!bodyChunks ||
                                      !!comment.stickerId ||
                                      belowImages.length !== 0) &&
                                      (!comment.deletedAt || !isCollapsed) && [
                                          <div
                                              key="text"
                                              className={cx(
                                                  styles.CommentBody__text,
                                                  inBlogs && styles["CommentBody__text--inBlogs"],
                                                  inGroups && styles["CommentBody__text--inGroups"],
                                              )}
                                          >
                                              {inGroups &&
                                                  comment.deletedAt &&
                                                  !showDeletedMessage && (
                                                      <DeletedCommentAlert
                                                          heading={
                                                              comment.deletedByRole ===
                                                              DeletedByRole.DeletedByAuthor
                                                                  ? BLOGPOST_COMMENT_DELETED_BY_AUTHOR
                                                                  : BLOGPOST_COMMENT_DELETED_BY_SOMEONE
                                                          }
                                                          deletedByRole={comment.deletedByRole}
                                                          deletedBy={comment.deletedBy?.username}
                                                          deletedAt={comment.deletedAt}
                                                          reason={comment.deletedReason}
                                                      />
                                                  )}

                                              {inBlogs &&
                                                  // admins are using strange JS code bookmarklet to fetch usernames
                                                  (comment.byAdmin ? (
                                                      <>
                                                          <strong>{ADMIN_USER}</strong>{" "}
                                                      </>
                                                  ) : (
                                                      <Link
                                                          className={
                                                              // class comment2-profile-link kept here to retain working some custom admin scripts
                                                              cx(
                                                                  styles.CommentBody__authorBlogs,
                                                                  "comment2-profile-link",
                                                              )
                                                          }
                                                          to={url(blogsProfileUrl, {
                                                              username: comment.authorUser.username,
                                                          })}
                                                      >
                                                          {comment.authorUser.username}
                                                      </Link>
                                                  ))}
                                              {inBlogs && isPostAuthor && !comment.byAdmin && (
                                                  <span
                                                      className={
                                                          styles.CommentBody__authorBadgeBlogs
                                                      }
                                                  >
                                                      {AUTHOR}
                                                  </span>
                                              )}

                                              {inGroups && (
                                                  <div
                                                      className={styles.CommentBody__nameAndBadges}
                                                  >
                                                      <CommentHeaderName
                                                          comment={comment}
                                                          inForum={inForum}
                                                          inGroups={inGroups}
                                                          isPostAuthor={isPostAuthor}
                                                          isMyAnonymousComment={
                                                              isMyAnonymousComment
                                                          }
                                                          moderators={moderators}
                                                          counsellor={null}
                                                      />
                                                      {comment.starsCount && (
                                                          <>
                                                              <Dot />
                                                              <StarsRating
                                                                  starsCount={comment.starsCount}
                                                              />
                                                          </>
                                                      )}
                                                  </div>
                                              )}

                                              {comment.repliedToComment && !inForum && (
                                                  <div
                                                      className={
                                                          styles.CommentBody__repliedToMessageWrapper
                                                      }
                                                  >
                                                      <CommentRepliedToMessage
                                                          clamp={2}
                                                          style={"light"}
                                                          repliedToBlogComment={
                                                              comment.repliedToComment
                                                          }
                                                          hasGreyBorder
                                                          routeNavigate={routeNavigate}
                                                      />
                                                  </div>
                                              )}

                                              <Emojify size={17} force={inBlogs}>
                                                  {bodyChunks}
                                              </Emojify>
                                          </div>,
                                      ],
                                  !!comment.stickerId &&
                                      (!comment.deletedAt || !isCollapsed) && [
                                          <Sticker
                                              className={cx(
                                                  styles.CommentBody__sticker,
                                                  inBlogs &&
                                                      styles["CommentBody__sticker--inBlogs"],
                                              )}
                                              key="stickerImg"
                                              id={comment.stickerId}
                                              size={StickerSize.Small}
                                          />,
                                      ],
                                  ((!comment.deletedAt && belowImages.length !== 0) ||
                                      (showDeletedStatus && belowImages.length !== 0)) && [
                                      <div
                                          key="photos"
                                          className={cx(
                                              styles.CommentBody__photos,
                                              inBlogs && styles["CommentBody__photoImg--inBlogs"],
                                          )}
                                      >
                                          {belowImages.map((photo, idx) => (
                                              <a
                                                  data-google-interstitial="false"
                                                  className={cx(
                                                      styles.CommentBody__photo,
                                                      idx === 0 &&
                                                          styles["CommentBody__photo--isFirst"],
                                                  )}
                                                  target="_blank"
                                                  rel="noopener"
                                                  key={photo.id}
                                                  href={photoUrl(
                                                      photo,
                                                      inForum
                                                          ? largestAvailablePhotoVersionOfPost(
                                                                photo,
                                                                "s1600x1600",
                                                            )
                                                          : "s1600x1600",
                                                  )}
                                              >
                                                  <Img
                                                      className={styles.CommentBody__photoImg}
                                                      src={photoUrl(
                                                          photo,
                                                          inForum
                                                              ? largestAvailablePhotoVersionOfPost(
                                                                    photo,
                                                                    "s600x600",
                                                                )
                                                              : "s305x150",
                                                      )}
                                                      height={photo.height}
                                                      width={photo.width}
                                                  />
                                              </a>
                                          ))}
                                      </div>,
                                  ],
                              ],
                          )
                        : !comment.deletedAt && (
                              <div className={styles.CommentBody__hiddenText}>
                                  {inForum ? FORUM_CANT_SEE_ANSWER : BLOGS_CANT_SEE_ANSWER}
                              </div>
                          )}
                </div>

                {showComplaints && inGroups && (
                    <Complaints
                        id={comment.id}
                        entity={ComplaintTargetType.PhotoblogComment}
                        deleteLabel={GROUP_COMMENT_REMOVE_COMPLAINTS}
                        adminTheme={isAdmin}
                    />
                )}
            </div>
        );
    }

    private getHashizeFn(groupId, inGroups, inForum) {
        if (inForum) {
            return (bodyChunk) => bodyChunk;
        } else if (inGroups && groupId) {
            return (bodyChunk) =>
                hashize(bodyChunk, (hash) => getGroupHashtagLink(parseInt(groupId, 10), hash));
        } else {
            return (bodyChunk) => hashize(bodyChunk, (hash) => url(blogsUrl, {}, { h: hash }));
        }
    }
}
